import { Injectable } from '@angular/core'
import { Resolve } from '@angular/router'
import { Apollo, gql } from 'apollo-angular'
import {
  applyTerminology,
  DEFAULT_TERMINOLOGY,
  TerminologyId,
  toTerminologyId,
} from 'common/terminology'
import { AuthService } from './auth/auth.service'
import { reportHandled } from './core/errors/issueTracker'
import { getTerminology } from './types/getTerminology'

/**
 * how long the first page after a login waits for the setting before it
 * paints in the default wording
 */
export const TERMINOLOGY_FIRST_WAIT_MS = 3000

const query = gql`
  query getTerminology {
    setting(where: { key: { _eq: "terminology" } }) {
      value
    }
  }
`

/**
 * The site's terminology (common/terminology.ts): which words the app uses
 * for the same things, e.g. "Scanner" or "Unit". One setting for the whole
 * site, chosen on the Settings page.
 *
 * It resolves the `home` route, so the first page already has the right
 * words (labels paint when a view is created, before a fetch started in
 * ngOnInit could answer). The wait is bounded and never fails a navigation:
 * if the setting has not arrived in time the page paints in the default
 * wording, the read is retried on later navigations without holding them,
 * and the `term` pipe switches the labels when it lands.
 */
@Injectable({
  providedIn: 'root',
})
export class TerminologyService implements Resolve<TerminologyId> {
  id: TerminologyId = DEFAULT_TERMINOLOGY

  /** the login `id` was read for (the token: another login may be another site in dev) */
  private loadedFor?: string
  /** the bounded wait of the first navigation after a login, shared by navigations that overlap it */
  private firstWait?: { token: string; settled: Promise<void>; over: boolean }
  private reading?: { token: string; done: Promise<void> }

  constructor(private apollo: Apollo, private auth: AuthService) {}

  /**
   * `text` in the site's terminology. For text written in the code only: a
   * string holding a name (a device called "Scanner 2") must not be passed.
   */
  apply(text: string): string {
    return applyTerminology(text, this.id)
  }

  /** the Settings page saved a terminology */
  set(id: string): void {
    this.id = toTerminologyId(id)
    this.loadedFor = this.auth.token || ''
  }

  resolve(): Promise<TerminologyId> {
    const token = this.auth.token || ''
    if (this.loadedFor === token) {
      return Promise.resolve(this.id)
    }
    if (!this.firstWait || this.firstWait.token !== token) {
      // a new login: its site's words are not known yet
      this.id = DEFAULT_TERMINOLOGY
      this.loadedFor = undefined
      const wait = {
        token,
        over: false,
        settled: Promise.race([
          this.read(token),
          new Promise<void>((resolve) =>
            setTimeout(resolve, TERMINOLOGY_FIRST_WAIT_MS),
          ),
        ]).then(() => {
          wait.over = true
        }),
      }
      this.firstWait = wait
    } else if (this.firstWait.over) {
      // already waited once for this login: paint now, keep trying
      this.read(token)
      return Promise.resolve(this.id)
    }
    return this.firstWait.settled.then(() => this.id)
  }

  /** one read at a time per login; never rejects */
  private read(token: string): Promise<void> {
    if (this.reading && this.reading.token === token) {
      return this.reading.done
    }
    const reading = {
      token,
      done: this.apollo
        .query<getTerminology>({ query })
        .toPromise()
        .then((result) => {
          if (result.errors) {
            throw result.errors[0]
          }
          // an answer for a login that has since changed is not this site's
          if ((this.auth.token || '') === token) {
            this.id = toTerminologyId(result.data?.setting[0]?.value)
            this.loadedFor = token
          }
        })
        .catch((e) => {
          console.warn('terminology unavailable, using the default wording', e)
          reportHandled(e, 'load the site terminology')
        })
        .then(() => {
          if (this.reading === reading) {
            this.reading = undefined
          }
        }),
    }
    this.reading = reading
    return reading.done
  }
}
